import { useEffect, useState, Fragment } from 'react';
import { FilterInputProps, FilterInputType } from '../types';
import { FlexBox } from '@ardoq/layout';
import { Text } from '@ardoq/typography';
import { NumericColumnFilterInput } from './NumericColumnFilterInput';
import { CheckboxColumnFilter } from './CheckboxColumnFilter';
import { DateColumnFilterInput } from './DateColumnFilterInput';
import { TextColumnFilter } from './TextColumnFilter';
import { ColumnFilterMultiselectWithLoadSuggestions } from './ColumnFilterMultiselectWithLoadSuggestions';
import { ValuePresenceColumnFilter } from './ValuePresenceColumnFilter';
import { UnknownColumnFilterInput } from './UnknownColumnFilterInput';
import { isEmpty } from 'lodash';
import { Maybe } from '@ardoq/common-helpers';
import type { ReportFilterSidebarContentLabels } from '../labels';

const DEFAULT_EMPTY_MESSAGE = 'No filters added yet';

type ReportFilterSidebarContentProps = {
  filtersToRender: FilterInputProps[];
  isReadOnly?: boolean;
  labels?: ReportFilterSidebarContentLabels;
};

export const ReportFilterSidebarContent = ({
  filtersToRender,
  isReadOnly,
  labels,
}: ReportFilterSidebarContentProps) => {
  // temporal dropdown fix for fullscreen presentations until we remove report filtering in presentations entirely
  const [selectMenuTarget, setSelectMenuTarget] = useState<Maybe<HTMLElement>>(
    document.body
  );

  useEffect(() => {
    const updateMenuTarget = () => {
      setSelectMenuTarget(
        (document.fullscreenElement as HTMLElement) ?? document.body
      );
    };

    document.addEventListener('fullscreenchange', updateMenuTarget);
    updateMenuTarget();

    return () =>
      document.removeEventListener('fullscreenchange', updateMenuTarget);
  }, []);

  const emptyMessage = labels?.emptyMessage ?? DEFAULT_EMPTY_MESSAGE;
  return (
    <>
      {isEmpty(filtersToRender) ? (
        <FlexBox justify={'center'} align={'center'} height={'full'}>
          <Text variant={'text2'} color={'textModerate'}>
            {emptyMessage}
          </Text>
        </FlexBox>
      ) : (
        <FlexBox flexDirection={'column'} gap={'medium'} padding={'medium'}>
          {filtersToRender.map(
            ({ inputType, props, additionalBottomContent }) => {
              if (inputType === FilterInputType.NUMERIC)
                return (
                  <Fragment key={props.column.key}>
                    <NumericColumnFilterInput
                      {...props}
                      menuTarget={selectMenuTarget}
                      isReadOnly={isReadOnly}
                      labels={labels?.numeric}
                      removeFilterButtonLabel={labels?.removeFilterButton}
                    />
                    {additionalBottomContent}
                  </Fragment>
                );

              if (inputType === FilterInputType.DATE)
                return (
                  <Fragment key={props.column.key}>
                    <DateColumnFilterInput
                      {...props}
                      menuTarget={selectMenuTarget}
                      isReadOnly={isReadOnly}
                      labels={labels?.date}
                      removeFilterButtonLabel={labels?.removeFilterButton}
                    />
                    {additionalBottomContent}
                  </Fragment>
                );
              if (inputType === FilterInputType.CHECKBOX)
                return (
                  <Fragment key={props.column.key}>
                    <CheckboxColumnFilter
                      menuTarget={selectMenuTarget}
                      isReadOnly={isReadOnly}
                      {...props}
                    />
                    {additionalBottomContent}
                  </Fragment>
                );

              if (
                inputType === FilterInputType.TEXT ||
                inputType === FilterInputType.EMAIL
              )
                return (
                  <Fragment key={props.column.key}>
                    <TextColumnFilter
                      menuTarget={selectMenuTarget}
                      isReadOnly={isReadOnly}
                      {...props}
                    />
                    {additionalBottomContent}
                  </Fragment>
                );

              if (inputType === FilterInputType.ASYNC_MULTISELECT) {
                return (
                  <Fragment key={props.column.key}>
                    <ColumnFilterMultiselectWithLoadSuggestions
                      menuTarget={selectMenuTarget}
                      isReadOnly={isReadOnly}
                      {...props}
                      labels={labels?.multiselect}
                      removeFilterButtonLabel={labels?.removeFilterButton}
                    />
                    {additionalBottomContent}
                  </Fragment>
                );
              }

              if (inputType === FilterInputType.UNKNOWN) {
                return (
                  <Fragment key={props.column.key}>
                    <UnknownColumnFilterInput
                      {...props}
                      labels={labels?.unknown}
                      removeFilterButtonLabel={labels?.removeFilterButton}
                    />
                    {additionalBottomContent}
                  </Fragment>
                );
              }

              return (
                <Fragment key={props.column.key}>
                  <ValuePresenceColumnFilter
                    menuTarget={selectMenuTarget}
                    isReadOnly={isReadOnly}
                    {...props}
                  />
                  {additionalBottomContent}
                </Fragment>
              );
            }
          )}
        </FlexBox>
      )}
    </>
  );
};
